import { Workbook, Worksheet } from 'exceljs';
import { addAoaSheet, downloadWorkbook } from './excelWorkbook';
import { formatRatePercent, platformCommissionPhrase, settlementFormulaLegendRows } from './settlementFormulas';

export type WeeklySettlementCafe = {
  cafe_id: string;
  cafe_name: string;
  completed_orders: number;
  cash_order_count?: number;
  gross_collected_jod: number;
  gross_via_platform_jod?: number;
  cash_collected_at_cafe_jod?: number;
  platform_commission_cash_jod?: number;
  platform_commission_digital_jod?: number;
  merchandise_subtotal_jod: number;
  service_fees_jod: number;
  platform_commission_jod: number;
  performance_fee_jod: number;
  marketing_owed_jod: number;
  platform_points_subsidy_jod?: number;
  platform_wallet_subsidy_jod?: number;
  cafe_adjustments_jod?: number;
  platform_commission_reversal_jod?: number;
  total_deductions_jod: number;
  net_payable_jod: number;
  dine_in_order_count?: number;
  dine_in_sales_jod?: number;
  dine_in_discount_jod?: number;
  dine_in_commission_recorded_jod?: number;
  delivery_fee_platform_jod?: number;
  adjustments?: {
    order_number: string;
    issue_type: string;
    refund_outcome: string;
    refund_applied_at: string;
    customer_refund_jod: number;
    refund_merchandise_jod: number;
    platform_commission_reversal_jod: number;
    cafe_deduction_jod: number;
  }[];
  marketing_breakdown: {
    promotion_id: string;
    title: string;
    promotion_type: string;
    overlap_days: number;
    daily_rate_jod: number;
    owed_jod: number;
  }[];
  orders: {
    order_number: string;
    created_at: string;
    payment_method: string;
    collected_by?: string;
    promotion_title: string;
    fulfillment_type?: string;
    dine_in_discount_jod?: number;
    dine_in_commission_jod?: number;
    merchandise_subtotal_jod: number;
    platform_commission_jod: number;
    platform_commission_rate?: number | null;
    delivery_fee_jod?: number;
    performance_fee_jod: number;
    gross_collected_jod: number;
    wallet_used_jod?: number;
    points_discount_jod?: number;
  }[];
};

export type WeeklySettlementResponse = {
  week_start: string;
  week_end: string;
  timezone: string;
  platform_commission_pct: number;
  dine_in_commission_default_pct?: number;
  cafes: WeeklySettlementCafe[];
  totals: {
    completed_orders: number;
    gross_collected_jod: number;
    gross_via_platform_jod?: number;
    cash_collected_at_cafe_jod?: number;
    platform_commission_cash_jod?: number;
    net_payable_jod: number;
    platform_commission_jod: number;
    performance_fee_jod: number;
    marketing_owed_jod: number;
    cafe_adjustments_jod?: number;
    platform_points_subsidy_jod?: number;
    platform_wallet_subsidy_jod?: number;
    total_deductions_jod: number;
    dine_in_order_count?: number;
    dine_in_sales_jod?: number;
    dine_in_discount_jod?: number;
    dine_in_commission_recorded_jod?: number;
    delivery_fee_platform_jod?: number;
  };
};

function colLetter(n: number): string {
  let s = '';
  let x = n;
  while (x > 0) {
    const m = (x - 1) % 26;
    s = String.fromCharCode(65 + m) + s;
    x = Math.floor((x - 1) / 26);
  }
  return s;
}

function coerceSheetNumbers(sheet: Worksheet) {
  sheet.eachRow((row) => {
    row.eachCell((cell) => {
      if (typeof cell.value === 'string' && /^-?\d+(\.\d+)?$/.test(cell.value)) {
        cell.value = Number(cell.value);
      }
    });
  });
}

function formatMoneySheet(sheet: Worksheet) {
  let headerRow = 0;
  const moneyCols = new Set<number>();
  const rateCols = new Set<number>();
  const letters: Record<string, string> = {};
  sheet.eachRow((row, idx) => {
    row.eachCell((cell) => {
      const name = String(cell.value || '');
      if (!headerRow && name.includes('(JOD)')) headerRow = idx;
    });
  });
  if (!headerRow) return { firstDataRow: 0, letters };
  sheet.getRow(headerRow).eachCell((cell, col) => {
    const name = String(cell.value || '');
    if (name.includes('(JOD)')) moneyCols.add(col);
    if (/rate/i.test(name)) rateCols.add(col);
    if (name.startsWith('Gross via platform')) letters.gross = colLetter(col);
    if (name.startsWith('Service fees')) letters.service = colLetter(col);
    if (name.startsWith('Platform commission total')) letters.platform = colLetter(col);
    if (name.startsWith('Performance add-on')) letters.performance = colLetter(col);
    if (name.startsWith('Marketing owed')) letters.marketing = colLetter(col);
    if (name.startsWith('Issue adjustments')) letters.issues = colLetter(col);
    if (name.startsWith('Total deductions')) letters.deductions = colLetter(col);
    if (name.startsWith('Net bank transfer (JOD)')) letters.net = colLetter(col);
  });
  sheet.eachRow((row, idx) => {
    if (idx <= headerRow) return;
    row.eachCell((cell, col) => {
      if (moneyCols.has(col) && typeof cell.value === 'number') cell.numFmt = '#,##0.000';
      if (rateCols.has(col) && typeof cell.value === 'number') cell.numFmt = '0.00%';
    });
  });
  if (letters.deductions && letters.gross && letters.service) {
    for (let r = headerRow + 1; r <= sheet.rowCount; r += 1) {
      const grossCell = sheet.getRow(r).getCell(letters.gross);
      if (typeof grossCell.value !== 'number') continue;
      const dedCell = sheet.getRow(r).getCell(letters.deductions);
      const netCell = sheet.getRow(r).getCell(letters.net);
      dedCell.value = {
        formula: `${letters.service}${r}+${letters.platform}${r}+${letters.performance}${r}+${letters.marketing}${r}+${letters.issues}${r}`,
        result: typeof dedCell.value === 'number' ? dedCell.value : 0,
      };
      dedCell.numFmt = '#,##0.000';
      netCell.value = {
        formula: `${letters.gross}${r}-${letters.deductions}${r}`,
        result: typeof netCell.value === 'number' ? netCell.value : 0,
      };
      netCell.numFmt = '#,##0.000';
    }
  }
  return { firstDataRow: headerRow + 1, letters };
}

function platformRate(data: WeeklySettlementResponse) {
  return platformCommissionPhrase(data.platform_commission_pct);
}

function dineInRate(data: WeeklySettlementResponse) {
  const shown = formatRatePercent(data.dine_in_commission_default_pct);
  return shown ? `${shown}%` : 'the café rate';
}

export function downloadWeeklySettlementExcel(
  filename: string,
  data: WeeklySettlementResponse,
  includeCafeColumn: boolean
) {
  const summaryRows: (string | number)[][] = [
    ['Weekly café settlement'],
    ['Week (Sun–Sat)', `${data.week_start} → ${data.week_end}`],
    ['Timezone', data.timezone],
    [`Platform commission`, `${platformRate(data)} of merchandise subtotal`],
    [
      'Dine-in commission',
      `Recorded only. Default ${dineInRate(data)} of merchandise after the dine-in discount, or the rate stored on the order. Not deducted from the net bank transfer.`,
    ],
    [
      'Net bank transfer',
      'Gross via platform (incl. Qahwahub credit & points on cash orders) − deductions. Dine-in commission is not a deduction.',
    ],
    [],
    includeCafeColumn
      ? [
          'Café',
          'Completed orders',
          'Gross via platform (JOD)',
          'Cash at café (JOD)',
          'Qahwahub credit subsidy (JOD)',
          'Points subsidy (JOD)',
          `Platform commission on cash (${platformRate(data)})`,
          'Merchandise subtotal (JOD)',
          'Service fees (JOD)',
          `Platform commission total (${platformRate(data)})`,
          'Performance add-on (JOD)',
          'Marketing owed (JOD)',
          'Issue adjustments (JOD)',
          'Total deductions (JOD)',
          'Net bank transfer (JOD)',
          'Delivery fee (QahwaHub) (JOD)',
          'Dine-in orders',
          'Dine-in sales (JOD)',
          'Dine-in discount (JOD)',
          'Dine-in commission recorded (JOD)',
        ]
      : [
          'Completed orders',
          'Gross via platform (JOD)',
          'Cash at café (JOD)',
          'Qahwahub credit subsidy (JOD)',
          'Points subsidy (JOD)',
          `Platform commission on cash (${platformRate(data)})`,
          'Merchandise subtotal (JOD)',
          'Service fees (JOD)',
          `Platform commission total (${platformRate(data)})`,
          'Performance add-on (JOD)',
          'Marketing owed (JOD)',
          'Issue adjustments (JOD)',
          'Total deductions (JOD)',
          'Net bank transfer (JOD)',
          'Delivery fee (QahwaHub) (JOD)',
          'Dine-in orders',
          'Dine-in sales (JOD)',
          'Dine-in discount (JOD)',
          'Dine-in commission recorded (JOD)',
        ],
  ];

  if (data.cafes.length === 0) {
    summaryRows.push(['No cafés in scope.']);
  } else {
    data.cafes.forEach((c) => {
      summaryRows.push(
        includeCafeColumn
          ? [
              c.cafe_name,
              c.completed_orders,
              (c.gross_via_platform_jod ?? 0).toFixed(2),
              (c.cash_collected_at_cafe_jod ?? 0).toFixed(2),
              (c.platform_wallet_subsidy_jod ?? 0).toFixed(2),
              (c.platform_points_subsidy_jod ?? 0).toFixed(2),
              (c.platform_commission_cash_jod ?? 0).toFixed(2),
              c.merchandise_subtotal_jod.toFixed(2),
              c.service_fees_jod.toFixed(2),
              c.platform_commission_jod.toFixed(2),
              c.performance_fee_jod.toFixed(2),
              c.marketing_owed_jod.toFixed(2),
              (c.cafe_adjustments_jod ?? 0).toFixed(2),
              c.total_deductions_jod.toFixed(2),
              c.net_payable_jod.toFixed(2),
              (c.delivery_fee_platform_jod ?? 0).toFixed(2),
              c.dine_in_order_count ?? 0,
              (c.dine_in_sales_jod ?? 0).toFixed(2),
              (c.dine_in_discount_jod ?? 0).toFixed(2),
              (c.dine_in_commission_recorded_jod ?? 0).toFixed(2),
            ]
          : [
              c.completed_orders,
              (c.gross_via_platform_jod ?? 0).toFixed(2),
              (c.cash_collected_at_cafe_jod ?? 0).toFixed(2),
              (c.platform_wallet_subsidy_jod ?? 0).toFixed(2),
              (c.platform_points_subsidy_jod ?? 0).toFixed(2),
              (c.platform_commission_cash_jod ?? 0).toFixed(2),
              c.merchandise_subtotal_jod.toFixed(2),
              c.service_fees_jod.toFixed(2),
              c.platform_commission_jod.toFixed(2),
              c.performance_fee_jod.toFixed(2),
              c.marketing_owed_jod.toFixed(2),
              (c.cafe_adjustments_jod ?? 0).toFixed(2),
              c.total_deductions_jod.toFixed(2),
              c.net_payable_jod.toFixed(2),
              (c.delivery_fee_platform_jod ?? 0).toFixed(2),
              c.dine_in_order_count ?? 0,
              (c.dine_in_sales_jod ?? 0).toFixed(2),
              (c.dine_in_discount_jod ?? 0).toFixed(2),
              (c.dine_in_commission_recorded_jod ?? 0).toFixed(2),
            ]
      );
    });
    summaryRows.push([]);
    summaryRows.push(['TOTALS']);
    summaryRows.push(
      includeCafeColumn
        ? [
            '',
            data.totals.completed_orders,
            (data.totals.gross_via_platform_jod ?? 0).toFixed(2),
            (data.totals.cash_collected_at_cafe_jod ?? 0).toFixed(2),
            (data.totals.platform_wallet_subsidy_jod ?? 0).toFixed(2),
            (data.totals.platform_points_subsidy_jod ?? 0).toFixed(2),
            (data.totals.platform_commission_cash_jod ?? 0).toFixed(2),
            '',
            '',
            data.totals.platform_commission_jod.toFixed(2),
            data.totals.performance_fee_jod.toFixed(2),
            data.totals.marketing_owed_jod.toFixed(2),
            (data.totals.cafe_adjustments_jod ?? 0).toFixed(2),
            data.totals.total_deductions_jod.toFixed(2),
            data.totals.net_payable_jod.toFixed(2),
            (data.totals.delivery_fee_platform_jod ?? 0).toFixed(2),
            data.totals.dine_in_order_count ?? 0,
            (data.totals.dine_in_sales_jod ?? 0).toFixed(2),
            (data.totals.dine_in_discount_jod ?? 0).toFixed(2),
            (data.totals.dine_in_commission_recorded_jod ?? 0).toFixed(2),
          ]
        : [
            data.totals.completed_orders,
            (data.totals.gross_via_platform_jod ?? 0).toFixed(2),
            (data.totals.cash_collected_at_cafe_jod ?? 0).toFixed(2),
            (data.totals.platform_wallet_subsidy_jod ?? 0).toFixed(2),
            (data.totals.platform_points_subsidy_jod ?? 0).toFixed(2),
            (data.totals.platform_commission_cash_jod ?? 0).toFixed(2),
            '',
            '',
            data.totals.platform_commission_jod.toFixed(2),
            data.totals.performance_fee_jod.toFixed(2),
            data.totals.marketing_owed_jod.toFixed(2),
            (data.totals.cafe_adjustments_jod ?? 0).toFixed(2),
            data.totals.total_deductions_jod.toFixed(2),
            data.totals.net_payable_jod.toFixed(2),
            (data.totals.delivery_fee_platform_jod ?? 0).toFixed(2),
            data.totals.dine_in_order_count ?? 0,
            (data.totals.dine_in_sales_jod ?? 0).toFixed(2),
            (data.totals.dine_in_discount_jod ?? 0).toFixed(2),
            (data.totals.dine_in_commission_recorded_jod ?? 0).toFixed(2),
          ]
    );
  }

  const orderRows: (string | number)[][] = [
    includeCafeColumn
      ? [
          'Café',
          'Order #',
          'Date',
          'Payment',
          'Collected by',
          'Promotion',
          'Merchandise (JOD)',
          'Qahwahub credit (JOD)',
          'Points subsidy (JOD)',
          `Platform commission (${platformRate(data)})`,
          'Performance fee (JOD)',
          'Gross (JOD)',
          'Stored platform rate',
          'Delivery fee (QahwaHub) (JOD)',
          'Fulfillment',
          'Dine-in discount (JOD)',
          'Dine-in commission recorded (JOD)',
        ]
      : [
          'Order #',
          'Date',
          'Payment',
          'Collected by',
          'Promotion',
          'Merchandise (JOD)',
          'Qahwahub credit (JOD)',
          'Points subsidy (JOD)',
          `Platform commission (${platformRate(data)})`,
          'Performance fee (JOD)',
          'Gross (JOD)',
          'Stored platform rate',
          'Delivery fee (QahwaHub) (JOD)',
          'Fulfillment',
          'Dine-in discount (JOD)',
          'Dine-in commission recorded (JOD)',
        ],
  ];

  data.cafes.forEach((c) => {
    if (c.orders.length === 0) {
      orderRows.push(includeCafeColumn ? [c.cafe_name, '—', 'No completed orders'] : ['—', 'No completed orders']);
      return;
    }
    c.orders.forEach((o) => {
      orderRows.push(
        includeCafeColumn
          ? [
              c.cafe_name,
              o.order_number,
              o.created_at,
              o.payment_method,
              o.collected_by || 'platform',
              o.promotion_title || '—',
              o.merchandise_subtotal_jod.toFixed(2),
              (o.wallet_used_jod ?? 0).toFixed(2),
              (o.points_discount_jod ?? 0).toFixed(2),
              o.platform_commission_jod.toFixed(2),
              o.performance_fee_jod.toFixed(2),
              o.gross_collected_jod.toFixed(2),
              o.platform_commission_rate ?? '',
              (o.delivery_fee_jod ?? 0).toFixed(2),
              o.fulfillment_type || 'pickup',
              (o.dine_in_discount_jod ?? 0).toFixed(2),
              (o.dine_in_commission_jod ?? 0).toFixed(2),
            ]
          : [
              o.order_number,
              o.created_at,
              o.payment_method,
              o.collected_by || 'platform',
              o.promotion_title || '—',
              o.merchandise_subtotal_jod.toFixed(2),
              (o.wallet_used_jod ?? 0).toFixed(2),
              (o.points_discount_jod ?? 0).toFixed(2),
              o.platform_commission_jod.toFixed(2),
              o.performance_fee_jod.toFixed(2),
              o.gross_collected_jod.toFixed(2),
              o.platform_commission_rate ?? '',
              (o.delivery_fee_jod ?? 0).toFixed(2),
              o.fulfillment_type || 'pickup',
              (o.dine_in_discount_jod ?? 0).toFixed(2),
              (o.dine_in_commission_jod ?? 0).toFixed(2),
            ]
      );
    });
  });

  const marketingRows: (string | number)[][] = [
    includeCafeColumn
      ? ['Café', 'Promotion', 'Type', 'Overlap days', 'Daily rate (JOD)', 'Owed (JOD)']
      : ['Promotion', 'Type', 'Overlap days', 'Daily rate (JOD)', 'Owed (JOD)'],
  ];

  data.cafes.forEach((c) => {
    if (c.marketing_breakdown.length === 0) {
      marketingRows.push(
        includeCafeColumn ? [c.cafe_name, '—', 'No marketing prorate this week'] : ['—', 'No marketing prorate this week']
      );
      return;
    }
    c.marketing_breakdown.forEach((m) => {
      marketingRows.push(
        includeCafeColumn
          ? [
              c.cafe_name,
              m.title,
              m.promotion_type,
              m.overlap_days,
              m.daily_rate_jod.toFixed(2),
              m.owed_jod.toFixed(2),
            ]
          : [m.title, m.promotion_type, m.overlap_days, m.daily_rate_jod.toFixed(2), m.owed_jod.toFixed(2)]
      );
    });
  });

  const adjustmentRows: (string | number)[][] = [
    includeCafeColumn
      ? [
          'Café',
          'Order #',
          'Issue type',
          'Refund outcome',
          'Refund applied',
          'Customer refund (JOD)',
          'Merchandise refund (JOD)',
          `Platform commission reversal (${platformRate(data)})`,
          'Café deduction (JOD)',
        ]
      : [
          'Order #',
          'Issue type',
          'Refund outcome',
          'Refund applied',
          'Customer refund (JOD)',
          'Merchandise refund (JOD)',
          `Platform commission reversal (${platformRate(data)})`,
          'Café deduction (JOD)',
        ],
  ];

  data.cafes.forEach((c) => {
    const rows = c.adjustments ?? [];
    if (rows.length === 0) {
      adjustmentRows.push(
        includeCafeColumn
          ? [c.cafe_name, '—', 'No issue adjustments this week']
          : ['—', 'No issue adjustments this week']
      );
      return;
    }
    rows.forEach((a) => {
      adjustmentRows.push(
        includeCafeColumn
          ? [
              c.cafe_name,
              a.order_number,
              a.issue_type,
              a.refund_outcome,
              a.refund_applied_at || '',
              a.customer_refund_jod.toFixed(2),
              a.refund_merchandise_jod.toFixed(2),
              a.platform_commission_reversal_jod.toFixed(2),
              a.cafe_deduction_jod.toFixed(2),
            ]
          : [
              a.order_number,
              a.issue_type,
              a.refund_outcome,
              a.refund_applied_at || '',
              a.customer_refund_jod.toFixed(2),
              a.refund_merchandise_jod.toFixed(2),
              a.platform_commission_reversal_jod.toFixed(2),
              a.cafe_deduction_jod.toFixed(2),
            ]
      );
    });
  });

  const wb = new Workbook();
  const summarySheet = addAoaSheet(wb, 'Summary', summaryRows);
  coerceSheetNumbers(summarySheet);
  const summaryLayout = formatMoneySheet(summarySheet);
  const dineInRows: (string | number)[][] = [
    ['Dine-in orders this week'],
    [
      'Commission on these rows is recorded only. It is not included in Summary total deductions or the net bank transfer.',
    ],
    [],
    includeCafeColumn
      ? [
          'Café',
          'Order #',
          'Date',
          'Merchandise (JOD)',
          'Dine-in discount (JOD)',
          'Dine-in commission recorded (JOD)',
          'Gross (JOD)',
        ]
      : [
          'Order #',
          'Date',
          'Merchandise (JOD)',
          'Dine-in discount (JOD)',
          'Dine-in commission recorded (JOD)',
          'Gross (JOD)',
        ],
  ];
  let dineInCount = 0;
  data.cafes.forEach((c) => {
    c.orders.forEach((o) => {
      if (String(o.fulfillment_type || '') !== 'dine_in') return;
      dineInCount += 1;
      dineInRows.push(
        includeCafeColumn
          ? [
              c.cafe_name,
              o.order_number,
              o.created_at,
              o.merchandise_subtotal_jod.toFixed(2),
              (o.dine_in_discount_jod ?? 0).toFixed(2),
              (o.dine_in_commission_jod ?? 0).toFixed(2),
              o.gross_collected_jod.toFixed(2),
            ]
          : [
              o.order_number,
              o.created_at,
              o.merchandise_subtotal_jod.toFixed(2),
              (o.dine_in_discount_jod ?? 0).toFixed(2),
              (o.dine_in_commission_jod ?? 0).toFixed(2),
              o.gross_collected_jod.toFixed(2),
            ]
      );
    });
  });
  if (dineInCount === 0) dineInRows.push(['No dine-in orders this week.']);

  addAoaSheet(wb, 'Formulas', settlementFormulaLegendRows(data.platform_commission_pct, summaryLayout));
  [orderRows, dineInRows, marketingRows, adjustmentRows].forEach((rows, index) => {
    const name = ['Orders', 'Dine-in', 'Marketing', 'Adjustments'][index];
    const sheet = addAoaSheet(wb, name, rows);
    coerceSheetNumbers(sheet);
    formatMoneySheet(sheet);
  });
  void downloadWorkbook(wb, filename);
}
